import React, { useState, useCallback } from 'react'; // Removed useEffect, useMemo as they are in the hook
import {
    Typography,
    Box as MuiBox,
    CircularProgress,
    Alert,
    Fade,
    useTheme,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TablePagination 
} from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloseIcon from '@mui/icons-material/Close';

import useAuth from '../../hooks/useAuth'; 
import PassiveSearchView from './PassiveSearchView'; 
import CandidateResultsTable from './ResultsTable/CandidateResultsTable'; 
import { useCandidateSearch } from './hooks/useCandidateSearch'; 
import { getScoreChipColor, getScoreColorForText } from './utils/candidateHelpers'; 
import { headCells, rowsPerPageOptionsConfig } from './config/passiveSearchTableConfig'; 
import HowSearchWorksDialogContent from './ResultsTable/HowSearchWorksDialogContent'; 
import SpinningCustomLogo from './SpinningCustomLogo'; 


const GLOBAL_HEADER_HEIGHT = theme => theme.spacing(8); 


const PassiveSearchContainer = () => {
    const theme = useTheme();
    const { isAuth: firebaseUser } = useAuth();

    const {
        jobDescription,
        handleJobDescriptionChange,
        handleSubmitSearch,
        sortedCandidates,
        paginatedCandidates,
        isProcessing,
        error,
        infoMessage,
        sortConfig,
        handleSortRequest,
        page,
        rowsPerPage,
        handleChangePage,
        handleChangeRowsPerPage,
        // rowsPerPageOptionsConfig is now imported directly
    } = useCandidateSearch(firebaseUser);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogContentElement, setDialogContentElement] = useState(null);

    const handleOpenDialog = (title, contentElement) => {
        setDialogTitle(title);
        setDialogContentElement(contentElement);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleHowSearchWorksClick = () => {
        handleOpenDialog("How Our Search Works", <HowSearchWorksDialogContent />);
    };

    const hasContentToDisplayBelowSearch = sortedCandidates.length > 0 || !!error || !!infoMessage;
    const shouldCenterPageContent = !isProcessing && !hasContentToDisplayBelowSearch; // True only for the very initial empty state
    const shouldCenterSearchAndSpinner = isProcessing && !hasContentToDisplayBelowSearch; // True if processing with no prior results/errors

    return (
        <MuiBox // Overall Page Container
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: shouldCenterPageContent || shouldCenterSearchAndSpinner ? 'center' : 'flex-start', 
                minHeight: '100vh',
                backgroundColor: theme.palette.background.default,
                overflowY: 'auto',
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            {/* Content Wrapper */}
            <MuiBox
                sx={{
                    width: '100%',
                    maxWidth: '1280px',
                    px: { xs: 2, sm: 3, md: 4 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: { xs: 2, sm: 2.5, md: 3 },
                    pt: (shouldCenterPageContent || shouldCenterSearchAndSpinner) ? 0 : GLOBAL_HEADER_HEIGHT(theme), 
                    pb: { xs: theme.spacing(4), sm: theme.spacing(6) }, 
                    flexGrow: 1,
                }}
            >
                {/* Search View Wrapper */}
                <MuiBox
                    sx={{
                        width: '100%',
                        maxWidth: '600px',
                        // Margin bottom to space it from spinner or results
                        mb: (shouldCenterPageContent && !isProcessing) ? 3.5 : 2, 
                    }}
                >
                    <PassiveSearchView
                        jobDescription={jobDescription}
                        onJobDescriptionChange={handleJobDescriptionChange}
                        onSubmit={handleSubmitSearch} 
                        isProcessing={isProcessing}
                        error={null} 
                        onHowSearchWorksClick={handleHowSearchWorksClick}
                    />
                </MuiBox>

                {isProcessing && (
                    <Fade in={isProcessing} timeout={300}>
                        <MuiBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 4 }}>
                            {/* Replace CircularProgress with SpinningCustomLogo */}
                            <SpinningCustomLogo size={64} />
                            <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                                Finding matching candidates...
                            </Typography>
                        </MuiBox>
                    </Fade>
                )}

                {!isProcessing && (error || infoMessage) && (
                    <MuiBox sx={{ width: '100%', maxWidth: '700px' }}>
                        {error && (
                            <Fade in={!!error} timeout={300}>
                                <MuiBox>
                                    <Alert severity="error" variant="outlined" sx={{ width: '100%', justifyContent: 'center' }}>
                                        {error}
                                    </Alert>
                                </MuiBox>
                            </Fade>
                        )}
                        {infoMessage && (
                            <Fade in={!!infoMessage && !error} timeout={300}>
                                <MuiBox>
                                    <Alert severity="info" variant="outlined" icon={<SearchOffIcon />} sx={{ width: '100%', justifyContent: 'center', mt: error ? 1.5 : 0 }}>
                                        {infoMessage}
                                    </Alert>
                                </MuiBox>
                            </Fade>
                        )}
                    </MuiBox>
                )}

                {!isProcessing && sortedCandidates.length > 0 && (
                    <MuiBox sx={{ width: '100%' }}>
                        <CandidateResultsTable
                            sortedCandidates={paginatedCandidates}
                            sortConfig={sortConfig}
                            handleSortRequest={handleSortRequest}
                            headCells={headCells} // From config import
                            // theme prop is not needed if CandidateResultsTable uses useTheme()
                            getScoreChipColor={getScoreChipColor} // From utils import
                            getScoreColorForText={(score) => getScoreColorForText(score, theme)} // From utils import
                        />
                        <TablePagination
                            component="div"
                            count={sortedCandidates.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={rowsPerPageOptionsConfig} // From config import
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
                            sx={{
                                mt: 0,
                                borderTop: `1px solid ${theme.palette.divider}`,
                                backgroundColor: theme.palette.background.paper,
                                borderBottomLeftRadius: '12px',
                                borderBottomRightRadius: '12px',
                            }}
                        />
                    </MuiBox>
                )}
            </MuiBox> {/* End of Content Wrapper */}

            {/* Dialog for "How Search Works" */}
            <Dialog open={openDialog} onClose={handleCloseDialog} PaperProps={{ sx: { borderRadius: '12px', minWidth: '300px', maxWidth: '600px' } }}>
                <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {dialogTitle}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2, typography: 'body2', color: 'text.secondary' }}>
                    {dialogContentElement /* Render the component directly */}
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button onClick={handleCloseDialog} variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </MuiBox>
    );
};

export default PassiveSearchContainer;
